import { CommonModule } from '@angular/common';
import {
  AfterViewInit,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  Input,
  OnChanges,
  OnDestroy,
  ViewChild,
} from '@angular/core';
import * as echarts from 'echarts/core';
import { BarChart } from 'echarts/charts';
import { AriaComponent, GridComponent, LegendComponent, TitleComponent, TooltipComponent } from 'echarts/components';
import { SVGRenderer } from 'echarts/renderers';
import { ECBasicOption } from 'echarts/types/dist/shared';
import {
  ECHARTS_BASE_OPTIONS,
  ECHARTS_GENERATIVE_THEME,
  ECHARTS_GENERATIVE_THEME_NAME,
} from '@shared/constants/custom-report-theme.constants';
import { IChatV2ComponentChunk } from '@shared/interfaces/ai-executor.interface';
import { severityColors } from '../../../../constants/charts.constants';
import { EchartsService } from '../../../../services/echarts.service';
import { LEGEND_ITEM_GAP_PX } from '../chart-legend.utility';
import {
  AXIS_LABEL_FONT_FAMILY,
  AXIS_LABEL_FONT_SIZE_PX,
  AXIS_LABEL_INTERVAL_ALL,
  AXIS_LABEL_MARGIN_PX,
  estimateNumericAxisTickLabelWidthPx,
} from '../chart-axis.utility';
import { getAxisLinePixelY, getRenderedContentBBox } from '../chart-measurement.utility';
import { ChartLayout } from '../chart-layout.constants';
import { toMarkdownTable } from '../generative-components.utils';

export interface IBarChartSeries {
  name: string;
  data: number[];
}

interface IAccessibleSeriesRow {
  seriesName: string;
  points: Array<{ category: string; value: number }>;
}

@Component({
  selector: 'app-ai-chat-bar-chart',
  templateUrl: './ai-chat-bar-chart.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [EchartsService],
  imports: [CommonModule],
  standalone: true,
})
export class AiChatBarChartComponent implements AfterViewInit, OnChanges, OnDestroy {
  @Input() public chunk: IChatV2ComponentChunk;

  @ViewChild('chartTarget') private chartTarget: ElementRef<HTMLDivElement>;

  public title: string = '';
  public accessibleSeriesData: IAccessibleSeriesRow[] = [];
  public chartHeight: number = 300;

  private chart: echarts.EChartsType;

  constructor(
    private changeDetectorRef: ChangeDetectorRef,
    private echartsService: EchartsService,
  ) {}

  public ngAfterViewInit(): void {
    this.initChart();
  }

  public ngOnChanges(): void {
    if (this.chart) {
      this.initChart();
    }
  }

  public ngOnDestroy(): void {
    this.echartsService.disposeAll();
  }

  public static serialize(data: Record<string, unknown>): string {
    const title = (data['title'] as string) ?? '';
    const xAxisLabel = (data['xAxisLabel'] as string) ?? '';
    const yAxisLabel = (data['yAxisLabel'] as string) ?? '';
    const categories = (data['categories'] as string[]) ?? [];
    const series = (data['series'] as IBarChartSeries[]) ?? [];

    const lines: string[] = [];
    if (title) {
      lines.push(title);
    }
    if (xAxisLabel || yAxisLabel) {
      lines.push(`${xAxisLabel} / ${yAxisLabel}`);
    }

    const columns = [xAxisLabel || 'Category', ...series.map((s: IBarChartSeries) => s.name)];
    const rows = categories.map((category: string, i: number) => [
      category,
      ...series.map((s: IBarChartSeries) => String(s.data[i] ?? 0)),
    ]);
    lines.push(toMarkdownTable(columns, rows));

    return lines.join('\n');
  }

  private withSeverityColor(series: IBarChartSeries): IBarChartSeries & { itemStyle?: { color: string } } {
    const color = (severityColors as Record<string, string | undefined>)[series.name.toLowerCase()];
    return color !== undefined ? { ...series, itemStyle: { color } } : series;
  }

  private initChart(): void {
    if (!this.chartTarget) {
      return;
    }

    const data = this.chunk?.data ?? {};
    const categories = (data['categories'] as string[]) ?? [];
    const series = (data['series'] as IBarChartSeries[]) ?? [];
    this.title = (data['title'] as string) ?? '';
    const xAxisLabel = (data['xAxisLabel'] as string) ?? '';
    const yAxisLabel = (data['yAxisLabel'] as string) ?? '';
    const stacked = (data['stacked'] as boolean) ?? false;

    this.accessibleSeriesData = series.map((s: IBarChartSeries) => ({
      seriesName: s.name,
      points: categories.map((cat: string, i: number) => ({ category: cat, value: s.data[i] ?? 0 })),
    }));

    // Single series: legend would just repeat what's already on the chart.
    const showLegend = series.length > 1;

    // grid.left uses the same tick-width estimate as nameGap so estimate error cancels out instead of pushing the name off-screen.
    const allValues = series.flatMap((s: IBarChartSeries) => s.data);
    const estimatedTickWidthPx = estimateNumericAxisTickLabelWidthPx(allValues);
    const yAxisNameGapPx = estimatedTickWidthPx + AXIS_LABEL_MARGIN_PX + ChartLayout.Y_AXIS_NAME_GAP_BREATHING_PX;
    const gridLeftPx = estimatedTickWidthPx + ChartLayout.Y_AXIS_NAME_LINE_HEIGHT_PX + ChartLayout.Y_AXIS_NAME_LEFT_BREATHING_PX;

    this.chartHeight = ChartLayout.PASS_1_SCRATCH_HEIGHT_PX;
    this.changeDetectorRef.detectChanges();

    echarts.use([BarChart, AriaComponent, GridComponent, LegendComponent, TitleComponent, TooltipComponent, SVGRenderer]);
    echarts.registerTheme(ECHARTS_GENERATIVE_THEME_NAME, ECHARTS_GENERATIVE_THEME);

    this.chart = this.echartsService.initialize(this.chartTarget.nativeElement, ECHARTS_GENERATIVE_THEME_NAME);

    const seriesOption = series.map((s: IBarChartSeries) => ({
      ...this.withSeverityColor(s),
      type: 'bar',
      ...(stacked ? { stack: 'total' } : {}),
    }));

    const xAxisLabelOption = {
      interval: AXIS_LABEL_INTERVAL_ALL,
      rotate: ChartLayout.X_AXIS_LABEL_ROTATE_DEGREES,
      fontSize: AXIS_LABEL_FONT_SIZE_PX,
      fontFamily: AXIS_LABEL_FONT_FAMILY,
    };

    // A partial setOption merge defers the re-render, so measuring right after reads stale values — always pass the full option.
    const buildOption = (
      gridHeightPx: number,
      xAxisName: string | undefined,
      xAxisNameGapPx: number,
      legendOption: Record<string, unknown>,
    ): ECBasicOption => ({
      ...ECHARTS_BASE_OPTIONS,
      legend: legendOption,
      grid: { top: ChartLayout.GRID_TOP_PX, height: gridHeightPx, left: gridLeftPx, right: '4%', containLabel: true },
      xAxis: {
        type: 'category',
        data: categories,
        name: xAxisName,
        nameLocation: 'middle',
        nameGap: xAxisNameGapPx,
        axisLabel: xAxisLabelOption,
      },
      yAxis: {
        type: 'value',
        name: yAxisLabel,
        nameLocation: 'middle',
        nameGap: yAxisNameGapPx,
        nameRotate: ChartLayout.Y_AXIS_NAME_ROTATE_DEGREES,
      },
      series: seriesOption,
    });

    // Pass 1: render without the x-axis name/legend to measure the labels' real extent.
    this.chart.setOption(buildOption(ChartLayout.PASS_1_GRID_HEIGHT_PLACEHOLDER_PX, undefined, 0, { show: false }), true);

    const axisLineY = getAxisLinePixelY(this.chart);
    const rotatedLabelsBBox = getRenderedContentBBox(this.chart);
    const realLabelHeightPx = rotatedLabelsBBox.y + rotatedLabelsBBox.height - axisLineY;
    const gridHeightPx = ChartLayout.GRID_PLOT_HEIGHT_PX + realLabelHeightPx + AXIS_LABEL_MARGIN_PX;
    const xAxisNameGapPx = realLabelHeightPx + AXIS_LABEL_MARGIN_PX + ChartLayout.X_AXIS_NAME_GAP_BREATHING_PX;
    const nameBottomPx =
      ChartLayout.GRID_TOP_PX + ChartLayout.GRID_PLOT_HEIGHT_PX + xAxisNameGapPx + ChartLayout.X_AXIS_NAME_HEIGHT_PX;
    const legendTopPx = nameBottomPx + ChartLayout.LEGEND_TOP_GAP_PX;
    const legendOption = showLegend
      ? { show: true, type: 'plain', orient: 'horizontal', top: legendTopPx, itemGap: LEGEND_ITEM_GAP_PX }
      : { show: false };

    // Pass 2: size the grid to the real label height, then add the x-axis name and legend.
    this.chart.setOption(buildOption(gridHeightPx, xAxisLabel, xAxisNameGapPx, legendOption), true);

    const finalBBox = getRenderedContentBBox(this.chart);
    this.chartHeight = finalBBox.y + finalBBox.height + ChartLayout.LEGEND_BOTTOM_PADDING_PX;
    this.changeDetectorRef.detectChanges();
    this.chart.resize();
  }
}
